// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import acceptJobQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/AcceptJobQuote";
import declineQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/DeclineQuote";
import deleteQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/DeleteQuote";
import submitQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SubmitQuote";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
    checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
    rfqJobQuotesPageUrl: getPageLinkByPageType(state, "RfqJobQuotesPage")?.url,
    cartState: getCurrentCartState(state),
    onePageCheckout: getSettingsCollection(state).cartSettings.onePageCheckout,
    onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
    // xng-change
    xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
});

const mapDispatchToProps = {
    deleteQuote,
    submitQuote,
    declineQuote,
    acceptJobQuote,
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsHeaderStyles {
    headerGridContainer?: GridContainerProps;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    deleteQuoteButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    declineQuoteButton?: ButtonPresentationProps;
    acceptQuoteButton?: ButtonPresentationProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    deleteQuoteClickable?: ClickableProps;
    submitQuoteClickable?: ClickableProps;
    declineQuoteClickable?: ClickableProps;
    acceptQuoteClickable?: ClickableProps;
    deleteQuoteModal?: TwoButtonModalStyles;
    cartNotificationModal?: TwoButtonModalStyles;
    // XNG-Change
    printLink?: LinkPresentationProps;
    deleteQuoteLink?: LinkPresentationProps;
    submitQuoteLink?: LinkPresentationProps;
    declineQuoteLink?: LinkPresentationProps;
    acceptQuoteLink?: LinkPresentationProps;
    declineQuoteModal?: TwoButtonModalStyles;
}

export const rfqQuoteDetailsHeaderStyles: RfqQuoteDetailsHeaderStyles = {
    headerGridContainer: {
        gap: 10,
    },
    deleteQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    submitQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    declineQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    acceptQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [2, 2, 2, 7, 6],
    },
    titleGridItem: {
        width: [10, 10, 10, 5, 6],
    },
    title: {
        variant: "h3",
        as: "h1",
        css: css`
            @media print {
                font-size: 11px;
            }
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

const styles = rfqQuoteDetailsHeaderStyles;

const RfqQuoteDetailsHeader: FC<Props> = ({
    quoteState,
    history,
    rfqMyQuotesPageUrl,
    checkoutShippingPageUrl,
    rfqJobQuotesPageUrl,
    cartState,
    onePageCheckout,
    onePageCheckoutPageUrl,
    deleteQuote,
    submitQuote,
    declineQuote,
    acceptJobQuote,
    // XNG-Change
    xGoogleAnalytics,
    fields: { show },
}) => {
    const toasterContext = useContext(ToasterContext);
    const [deleteQuoteModalIsOpen, setDeleteQuoteModalIsOpen] = useState(false);
    const [cartNotificationModalIsOpen, setCartNotificationModalIsOpen] = useState(false);
    // Xng-Changes
    const [declineQuoteModalIsOpen, setDeclineQuoteModalIsOpen] = useState(false);

    /**
     * XNG-Changes: hiding header/actions until quote is loaded to prevent showing wrong buttons
     */
    if (!quoteState.value || quoteState.isLoading) {
        return null;
    }

    const quote = quoteState.value;

    const deleteQuoteClickHandler = () => {
        setDeleteQuoteModalIsOpen(true);
    };

    // Xng-Changes
    const declineQuoteClickHandler = () => {
        setDeclineQuoteModalIsOpen(true);
    };

    const submitDeleteQuoteHandler = () => {
        deleteQuote({
            quoteId: quote.id,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Quote deleted."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                this.onSuccess?.();
            },
        });
    };

    const cancelDeleteQuoteHandler = () => {
        setDeleteQuoteModalIsOpen(false);
    };

    // Xng-Changes
    const cancelDeclineQuoteHandler = () => {
        setDeclineQuoteModalIsOpen(false);
    };

    const submitCartNotificationHandler = () => {
        // Xng-Changes
        trackQuoteAcceptedEvent();
        redirectToCheckout();
    };

    const cancelCartNotificationHandler = () => {
        setCartNotificationModalIsOpen(false);
    };

    // XNG-Changes
    const trackQuoteEvent = (eventFlag: boolean | undefined, eventName: string | undefined, clickText?: string) => {
        if (!eventFlag || !eventName) {
            return;
        }

        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData: any = {
            event: eventName,
            quote_name: quote.quoteNumber || "",
            value: quote.orderGrandTotal || quote.orderSubTotal || 0,
            affiliation: xGoogleAnalytics.affiliation || "",
            sales_rep: xGoogleAnalytics.salesRep || "",
            is_rep: xGoogleAnalytics.isRep || "",
            bill_to_num: xGoogleAnalytics.billToNum || "",
            ship_to_num: xGoogleAnalytics.shipToNum || "",
            cust_type: xGoogleAnalytics.customerType || "",
            login_type: xGoogleAnalytics.loginType || "",
            warehouse: xGoogleAnalytics.warehouse || "",
        };

        if (clickText) {
            googleAnalyticsData.click_text = clickText;
        }

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);
    };

    const trackQuoteProposedEvent = () => {
        trackQuoteEvent(xGoogleAnalytics?.quoteProposedEvent, xGoogleAnalytics?.quoteProposedEventName, submitQuoteLabel);
    };

    const trackQuoteAcceptedEvent = () => {
        trackQuoteEvent(xGoogleAnalytics?.quoteAcceptedEvent, xGoogleAnalytics?.quoteAcceptedEventName, acceptQuoteLabel);
    };

    const trackQuoteDeclinedEvent = () => {
        trackQuoteEvent(xGoogleAnalytics?.quoteDeclinedEvent, xGoogleAnalytics?.quoteDeclinedEventName);
    };

    const submitQuoteClickHandler = () => {
        submitQuote({
            quote,
            onSuccess: () => {
                // XNG-Changes
                trackQuoteProposedEvent();
                toasterContext.addToast({ body: translate("Quote submitted."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    const submitDeclineQuoteHandler = () => {
        declineQuote({
            quote,
            onSuccess: () => {
                // XNG-Changes
                trackQuoteDeclinedEvent();
                toasterContext.addToast({ body: translate("Quote declined."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    const acceptQuoteClickHandler = () => {
        if (quote.isJobQuote) {
            acceptJobQuote({
                quote,
                onSuccess: () => {
                    // XNG-Changes
                    trackQuoteAcceptedEvent();
                    toasterContext.addToast({ body: translate("Quote accepted."), messageType: "success" });
                    redirectToJobQuotes();
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.();
                    }
                },
            });
        } else if (cartState.value?.lineCount === 0) {
            // XNG-Changes
            trackQuoteAcceptedEvent();
            redirectToCheckout();
        } else {
            setCartNotificationModalIsOpen(true);
        }
    };

    const redirectToMyQuotes = () => {
        if (rfqMyQuotesPageUrl) {
            history.push(rfqMyQuotesPageUrl);
        }
    };

    const redirectToCheckout = () => {
        if (onePageCheckout && onePageCheckoutPageUrl) {
            history.push(`${onePageCheckoutPageUrl}?cartId=${quote.id}`);
            return;
        }

        if (checkoutShippingPageUrl) {
            history.push(`${checkoutShippingPageUrl}?cartId=${quote.id}`);
        }
    };

    const redirectToJobQuotes = () => {
        if (rfqJobQuotesPageUrl) {
            history.push(rfqJobQuotesPageUrl);
        }
    };

    const expirationDateIsGreaterThanCurrentDate = () => {
        if (!quote || !quote.expirationDate) {
            return true;
        }

        const expirationDate = new Date(quote.expirationDate.toString());
        const currentDate = new Date();

        expirationDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        return expirationDate >= currentDate;
    };

    const quoteType = quote.isJobQuote ? "Job Quote" : "Sales Quote";
    const printLabel = translate("Print");
    const deleteQuoteLabel = translate(`Delete ${quoteType}`);
    const submitQuoteLabel = translate(`Submit ${quoteType}`);
    const declineQuoteLabel = translate(`Decline ${quoteType}`);
    const acceptQuoteLabel = translate(`Accept ${quoteType}`);
    const canBeSubmittedOrDeleted = quote.isSalesperson && quote.isEditable;
    const expirationDateIsValid = expirationDateIsGreaterThanCurrentDate();
    const canBeDeclined = !quote.isSalesperson && quote.status === "QuoteProposed" && expirationDateIsValid;
    const canBeAccepted =
        (!quote.failedToGetRealTimeInventory || quote.quoteLineCollection?.every(o => o.canBackOrder)) &&
        !quote.isSalesperson &&
        (quote.status === "QuoteProposed" || quote.status === "AwaitingApproval") &&
        expirationDateIsValid;
    // XNG-Change: Structure
    return (
        <>
            <GridContainer {...styles.headerGridContainer}>
                {(show === "title" || show === "both") && (
                    <GridItem {...styles.titleGridItem}>
                        <Typography {...styles.title}>{`${translate(quoteType)} ${quote.quoteNumber}`}</Typography>
                    </GridItem>
                )}
                {(show === "actions" || show === "both") && (
                    <GridItem {...styles.buttonGridItem}>
                        {/* <Hidden {...styles.menuHiddenContainer}>
                        <OverflowMenu {...styles.narrowOverflowMenu}>
                            <Clickable {...styles.printClickable} onClick={openPrintDialog} aria-label={printLabel}>
                                {printLabel}
                            </Clickable>
                            {canBeSubmittedOrDeleted && (
                                <>
                                    <Clickable
                                        {...styles.deleteQuoteClickable}
                                        onClick={deleteQuoteClickHandler}
                                        aria-label={deleteQuoteLabel}
                                    >
                                        {deleteQuoteLabel}
                                    </Clickable>
                                    <Clickable
                                        {...styles.submitQuoteClickable}
                                        onClick={submitQuoteClickHandler}
                                        aria-label={submitQuoteLabel}
                                    >
                                        {submitQuoteLabel}
                                    </Clickable>
                                </>
                            )}
                            {canBeDeclined && (
                                <Clickable
                                    {...styles.declineQuoteClickable}
                                    onClick={declineQuoteClickHandler}
                                    aria-label={declineQuoteLabel}
                                >
                                    {declineQuoteLabel}
                                </Clickable>
                            )}
                            {canBeAccepted && (
                                <Clickable
                                    {...styles.acceptQuoteClickable}
                                    onClick={acceptQuoteClickHandler}
                                    aria-label={acceptQuoteLabel}
                                >
                                    {acceptQuoteLabel}
                                </Clickable>
                            )}
                        </OverflowMenu>
                    </Hidden>
                    <Hidden {...styles.buttonsHiddenContainer}>
                        <Button {...styles.printButton} onClick={openPrintDialog} aria-label={printLabel}>
                            {printLabel}
                        </Button>
                        {canBeSubmittedOrDeleted && (
                            <>
                                <Button
                                    {...styles.deleteQuoteButton}
                                    onClick={deleteQuoteClickHandler}
                                    data-test-selector="rfqQuoteDetails_deleteQuote"
                                    aria-label={deleteQuoteLabel}
                                >
                                    {deleteQuoteLabel}
                                </Button>
                                <Button
                                    {...styles.submitQuoteButton}
                                    onClick={submitQuoteClickHandler}
                                    data-test-selector="rfqQuoteDetails_submitQuote"
                                    aria-label={submitQuoteLabel}
                                >
                                    {submitQuoteLabel}
                                </Button>
                            </>
                        )}
                        {canBeDeclined && (
                            <Button
                                {...styles.declineQuoteButton}
                                onClick={declineQuoteClickHandler}
                                data-test-selector="rfqQuoteDetails_declineQuote"
                                aria-label={declineQuoteLabel}
                            >
                                {declineQuoteLabel}
                            </Button>
                        )}
                        {canBeAccepted && (
                            <Button
                                {...styles.acceptQuoteButton}
                                onClick={acceptQuoteClickHandler}
                                data-test-selector="rfqQuoteDetails_acceptQuote"
                                aria-label={acceptQuoteLabel}
                            >
                                {acceptQuoteLabel}
                            </Button>
                        )}
                    </Hidden> */}
                        <Link {...styles.printLink} onClick={openPrintDialog} aria-label={printLabel}>
                            {printLabel}
                        </Link>
                        {canBeSubmittedOrDeleted && (
                            <>
                                <Link
                                    {...styles.deleteQuoteLink}
                                    onClick={deleteQuoteClickHandler}
                                    aria-label={deleteQuoteLabel}
                                >
                                    {deleteQuoteLabel}
                                </Link>
                                <Link
                                    {...styles.submitQuoteLink}
                                    onClick={submitQuoteClickHandler}
                                    aria-label={submitQuoteLabel}
                                >
                                    {submitQuoteLabel}
                                </Link>
                            </>
                        )}
                        {canBeDeclined && (
                            <Link
                                {...styles.declineQuoteLink}
                                onClick={declineQuoteClickHandler}
                                aria-label={declineQuoteLabel}
                            >
                                {declineQuoteLabel}
                            </Link>
                        )}
                        {canBeAccepted && (
                            <Link
                                {...styles.acceptQuoteLink}
                                onClick={acceptQuoteClickHandler}
                                aria-label={acceptQuoteLabel}
                            >
                                {acceptQuoteLabel}
                            </Link>
                        )}
                    </GridItem>
                )}
            </GridContainer>
            {/* Xng-Changes */}
            <TwoButtonModal
                {...styles.declineQuoteModal}
                modalIsOpen={declineQuoteModalIsOpen}
                headlineText={translate("Decline Quote")}
                messageText={siteMessage("Custom_Rfq_DeclineQuoteConfirmation")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Decline")}
                onCancel={cancelDeclineQuoteHandler}
                onSubmit={submitDeclineQuoteHandler}
                submitTestSelector="submitDeclineQuote"
            />
            <TwoButtonModal
                {...styles.deleteQuoteModal}
                modalIsOpen={deleteQuoteModalIsOpen}
                headlineText={translate("Delete Quote")}
                messageText={siteMessage("Rfq_DeleteQuoteConfirmation")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={cancelDeleteQuoteHandler}
                onSubmit={submitDeleteQuoteHandler}
                submitTestSelector="submitDeleteQuote"
            />
            <TwoButtonModal
                {...styles.cartNotificationModal}
                modalIsOpen={cartNotificationModalIsOpen}
                headlineText={translate("Cart Notification")}
                messageText={siteMessage("Rfq_CartNotificationMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={cancelCartNotificationHandler}
                onSubmit={submitCartNotificationHandler}
                submitTestSelector="submitCartNotification"
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqQuoteDetailsHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["RfqQuoteDetailsPage"],
        fieldDefinitions: [
            // XNG-Changes
            {
                name: "show",
                displayName: "Show",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Title", value: "title" },
                    { displayName: "Actions", value: "actions" },
                    { displayName: "Both", value: "both" },
                ],
                fieldType: "General",
                defaultValue: "title",
            },
        ],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;
